import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { CustomerGroupsFacade } from '@symphony-solo/domain/customer-groups/src';
import { ToastFacade } from '@symphony-solo/domain/toast/src';
import { AppStateFailureBuilder, AppStateFailureItemWithErrorCode, FailureType } from '@symphony-solo/shared/common-state/src';
import { ErrorCode } from '@symphony-solo/shared/error-handling/src';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { ProductService } from '../services';
import {
  getProductDetailsAction,
  getProductDetailsErrorAction,
  getProductDetailsSuccessAction,
  productBarcodeScannedAction,
} from './select-product.actions';

@Injectable()
export class SelectProductEffects {
  constructor(
    private action$: Actions,
    private productService: ProductService,
    private toastFacade: ToastFacade,
    private customerGroupsFacade: CustomerGroupsFacade
  ) {}

  protected productBarcodeScannedAction$ = createEffect(() =>
    this.action$.pipe(
      ofType(productBarcodeScannedAction),
      switchMap((payload) => {
        return of(getProductDetailsAction({ barcode: payload.barcode }));
      })
    )
  );

  protected getProductDetails$ = createEffect(() =>
    this.action$.pipe(
      ofType(getProductDetailsAction),
      withLatestFrom(this.customerGroupsFacade.selectedCustomerGroup$),
      switchMap(([payload, customerGroup]) => {
        return this.productService.getProductDetails(payload.barcode, customerGroup.priceLevel).pipe(
          map((productDetails) => {
            if (productDetails.prices.length !== 0) {
              return getProductDetailsSuccessAction({ productDetails });
            }

            const errorMessage = 'No price for product';
            this.toastFacade.showToastErrorMessage(errorMessage);

            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.General)
              .addFailure(new AppStateFailureItemWithErrorCode(errorMessage, ErrorCode.NoPriceForProduct))
              .build();

            return getProductDetailsErrorAction({ failure });
          }),
          catchError(() => {
            const errorMessage = 'Barcode not recognised please pay at till';
            this.toastFacade.showToastErrorMessage(errorMessage);

            const failure = new AppStateFailureBuilder()
              .setFailureType(FailureType.General)
              .addFailure(new AppStateFailureItemWithErrorCode(errorMessage, ErrorCode.InvalidProductBarcode))
              .build();

            return of(getProductDetailsErrorAction({ failure }));
          })
        );
      })
    )
  );
}
